import type { CompanySettings } from '../types';
import type { PayrollBuilderFreqKind } from './payrollBuilderFilters';

export type PayrollCycleOption = {
  kind: PayrollBuilderFreqKind;
  label: string;
};

/** Labels for tenant payroll cycles (no OOH branding). */
export const PAYROLL_CYCLE_LABELS: Record<PayrollBuilderFreqKind, string> = {
  weekly: 'Weekly',
  fortnightly: 'Bi-weekly',
  'ooh-hourly': 'Hourly 29–28',
  monthly: 'Salaried Monthly',
};

export type PayrollCycleFlags = Pick<
  CompanySettings,
  | 'payrollCycleWeeklyEnabled'
  | 'payrollCycleFortnightlyEnabled'
  | 'payrollCycleHourly2928Enabled'
  | 'payrollCycleSalariedMonthlyEnabled'
>;

/** Defaults when settings are missing: all cycles available (backward compatible). */
export function defaultPayrollCycleFlags(): Required<PayrollCycleFlags> {
  return {
    payrollCycleWeeklyEnabled: true,
    payrollCycleFortnightlyEnabled: true,
    payrollCycleHourly2928Enabled: true,
    payrollCycleSalariedMonthlyEnabled: true,
  };
}

export function resolvePayrollCycleFlags(settings?: PayrollCycleFlags | null): Required<PayrollCycleFlags> {
  const d = defaultPayrollCycleFlags();
  if (!settings) return d;
  return {
    payrollCycleWeeklyEnabled: settings.payrollCycleWeeklyEnabled ?? d.payrollCycleWeeklyEnabled,
    payrollCycleFortnightlyEnabled: settings.payrollCycleFortnightlyEnabled ?? d.payrollCycleFortnightlyEnabled,
    payrollCycleHourly2928Enabled: settings.payrollCycleHourly2928Enabled ?? d.payrollCycleHourly2928Enabled,
    payrollCycleSalariedMonthlyEnabled:
      settings.payrollCycleSalariedMonthlyEnabled ?? d.payrollCycleSalariedMonthlyEnabled,
  };
}

/** Only cycles enabled for the tenant, in display order. */
export function getEnabledPayrollCycleOptions(settings?: PayrollCycleFlags | null): PayrollCycleOption[] {
  const f = resolvePayrollCycleFlags(settings);
  const all: PayrollCycleOption[] = [
    { kind: 'weekly', label: PAYROLL_CYCLE_LABELS.weekly },
    { kind: 'fortnightly', label: PAYROLL_CYCLE_LABELS.fortnightly },
    { kind: 'ooh-hourly', label: PAYROLL_CYCLE_LABELS['ooh-hourly'] },
    { kind: 'monthly', label: PAYROLL_CYCLE_LABELS.monthly },
  ];
  return all.filter((o) => {
    switch (o.kind) {
      case 'weekly':
        return f.payrollCycleWeeklyEnabled;
      case 'fortnightly':
        return f.payrollCycleFortnightlyEnabled;
      case 'ooh-hourly':
        return f.payrollCycleHourly2928Enabled;
      case 'monthly':
        return f.payrollCycleSalariedMonthlyEnabled;
      default:
        return false;
    }
  });
}

export function freqKindDisplayLabel(kind: string | null | undefined): string {
  if (!kind) return '—';
  if (kind === 'ooh-hourly') return PAYROLL_CYCLE_LABELS['ooh-hourly'];
  if (kind === 'monthly') return PAYROLL_CYCLE_LABELS.monthly;
  if (kind === 'fortnightly') return PAYROLL_CYCLE_LABELS.fortnightly;
  if (kind === 'weekly') return PAYROLL_CYCLE_LABELS.weekly;
  return kind;
}
